import useSWR from "swr";
import { useTranslation } from "react-i18next";
import { MapContainer, TileLayer, Circle, Tooltip as LTooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { fetcher } from "@/lib/api";

const SALNES_CENTER = [42.53, -8.82];
const STOPS = [
  "#EDF7F1",
  "#B8E4CB",
  "#7ECBA0",
  "#3AB47A",
  "#0F8B54",
  "#0B4C7A",
];

function colorFor(v, max) {
  if (!max || v <= 0) return STOPS[0];
  const ratio = Math.max(0, Math.min(1, v / max));
  const idx = Math.min(STOPS.length - 1, Math.floor(ratio * STOPS.length));
  return STOPS[idx];
}

function fmtInt(n) {
  return new Intl.NumberFormat("es-ES").format(Math.round(n || 0));
}
function fmtDec(n, d = 2) {
  return new Intl.NumberFormat("es-ES", {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  }).format(n || 0);
}

export default function ImpactoCarbonMap() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("es") ? "es" : "gl";
  const { data } = useSWR("/public/impacto/carbon-by-concello", fetcher, {
    revalidateOnFocus: false,
    refreshInterval: 10 * 60 * 1000,
  });

  const items = data?.items || [];
  const totals = data?.totals || {};
  const factor = data?.factor || {};
  const maxCo2e = items.reduce((m, c) => Math.max(m, c.co2e_kg || 0), 0);

  const sortedRank = [...items].sort(
    (a, b) => (b.co2e_kg || 0) - (a.co2e_kg || 0)
  );

  return (
    <div
      className="grid lg:grid-cols-12 gap-6 lg:gap-8"
      data-testid="impacto-carbon-map"
    >
      <div className="lg:col-span-7">
        <div className="rounded-2xl border hairline bg-white overflow-hidden">
          <div className="px-5 pt-4 pb-2 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-salnes-stone">
                {lang === "es"
                  ? "Emisiones CO₂e por concello · YTD"
                  : "Emisións CO₂e por concello · YTD"}
              </div>
              <div className="font-serif text-lg text-salnes-ink mt-0.5">
                {fmtDec(totals.co2e_t || 0, 3)}
                <span className="text-[11px] text-salnes-stone ml-1">t CO₂e</span>
              </div>
            </div>
            <div
              className="flex items-center gap-1 text-[10px] text-salnes-stone"
              data-testid="impacto-carbon-legend"
            >
              <span>0</span>
              {STOPS.map((c, i) => (
                <span
                  key={i}
                  className="inline-block h-3 w-3.5"
                  style={{ background: c }}
                />
              ))}
              <span className="tabular-nums">{fmtInt(maxCo2e)}</span>
            </div>
          </div>
          <div className="h-[360px]">
            <MapContainer
              center={SALNES_CENTER}
              zoom={10}
              scrollWheelZoom={false}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                attribution="&copy; OSM · CARTO"
              />
              {items.map((c) => {
                if (!c.center) return null;
                const fill = colorFor(c.co2e_kg || 0, maxCo2e);
                const rMin = 2200;
                const rMax = 5800;
                const ratio = maxCo2e > 0 ? (c.co2e_kg || 0) / maxCo2e : 0;
                const radius = rMin + ratio * (rMax - rMin);
                return (
                  <Circle
                    key={c.key}
                    center={c.center}
                    radius={radius}
                    pathOptions={{
                      color: fill,
                      weight: 1.5,
                      fillColor: fill,
                      fillOpacity: 0.6,
                    }}
                  >
                    <LTooltip direction="top" opacity={0.95} sticky>
                      <div style={{ font: "12px Inter, sans-serif" }}>
                        <div
                          style={{
                            fontFamily: "'Playfair Display', serif",
                            fontSize: 13,
                            color: "#0B4C7A",
                            fontWeight: 600,
                          }}
                        >
                          {c.name}
                        </div>
                        <div style={{ color: "#374151", marginTop: 4 }}>
                          <b>{fmtDec(c.co2e_kg, 1)}</b> kg CO₂e
                        </div>
                        <div style={{ color: "#6B7280", fontSize: 11 }}>
                          {fmtInt(c.kg_total)} kg{" "}
                          {lang === "es" ? "recogidos" : "recollidos"}
                        </div>
                      </div>
                    </LTooltip>
                  </Circle>
                );
              })}
            </MapContainer>
          </div>
        </div>
      </div>

      <div className="lg:col-span-5">
        <div className="rounded-2xl border hairline bg-white p-5 h-full">
          <div className="text-[10px] uppercase tracking-[0.22em] text-salnes-stone mb-3">
            {lang === "es"
              ? "Ranking por concello"
              : "Ranking por concello"}
          </div>
          <div className="space-y-1.5">
            {sortedRank.map((c) => {
              const pct =
                maxCo2e > 0 ? ((c.co2e_kg || 0) / maxCo2e) * 100 : 0;
              return (
                <div
                  key={c.key}
                  className="grid grid-cols-[minmax(130px,150px)_1fr_90px] items-center gap-3 text-xs"
                  data-testid={`impacto-carbon-rank-${c.key}`}
                >
                  <div className="truncate text-salnes-ink">{c.name}</div>
                  <div className="h-1.5 bg-salnes-sand/60 rounded-full overflow-hidden">
                    <div
                      className="h-full"
                      style={{
                        width: `${pct}%`,
                        background: colorFor(c.co2e_kg || 0, maxCo2e),
                      }}
                    />
                  </div>
                  <div className="text-right tabular-nums text-salnes-stone">
                    {fmtInt(c.co2e_kg)} kg
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 pt-3 border-t hairline text-[10px] text-salnes-stone italic">
            {lang === "es"
              ? `Factor ${fmtDec(factor.value_kgco2e_per_t, 1)} kg CO₂e/t · ${
                  factor.source || "MITECO"
                }`
              : `Factor ${fmtDec(factor.value_kgco2e_per_t, 1)} kg CO₂e/t · ${
                  factor.source || "MITECO"
                }`}
          </div>
        </div>
      </div>
    </div>
  );
}
